import * as React from 'react';

import { Subscription } from '@packages/common';
import {
  ClickoutContext,
  RefinementsContext,
  useRefinementsEvents,
  WishlistHelperContext,
} from '@packages/shared/context';

import { DyContext } from '@lib/dy/DyProvider';

import { DyRecommendationCampaign, DyRecommendationSlot } from './types';
import { createDyRecommendationRules } from './helpers';
import { RecommendationChannel } from '../types';

export const dyRecommendationCampaigns: Record<RecommendationChannel, string> = {
  [RecommendationChannel.SEARCH_PAGE]: '#serp_slider_above-title',
  [RecommendationChannel.WISHLIST]: '#wishlist_slider_belowproducts',
  [RecommendationChannel.MARKETING_BOX]: '#quickview_slider_above-title',
  [RecommendationChannel.CATEGORY_PAGE]: '#category_slider_within-grid',
};

interface State {
  loading: boolean;
  hasError: boolean;
  title: string;
  recommendations: DyRecommendationSlot[];
  decisionId: string;
}

type Action =
  | { type: 'LOADING_STARTED' }
  | { type: 'LOADING_COMPLETED'; title: string; recommendations: DyRecommendationSlot[]; decisionId: string }
  | { type: 'LOADING_ERRORED' };

const initialDyRecommendations = { title: '', recommendations: [], decisionId: '' };
const initialState: State = {
  ...initialDyRecommendations,
  loading: false,
  hasError: false,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'LOADING_STARTED':
      return { ...state, loading: true, hasError: false };
    case 'LOADING_COMPLETED':
      const { title, recommendations, decisionId } = action;
      return {
        ...state,
        title,
        recommendations,
        decisionId,
        loading: false,
        hasError: false,
      };
    case 'LOADING_ERRORED':
      return { ...state, loading: false, hasError: true };
    default:
      return state;
  }
}

interface DyRecommendationParams {
  categoryTitle: string;
  recommendationChannel: RecommendationChannel;
}

type UseDyRecommendationsResult = {
  title: string;
  recommendations: DyRecommendationSlot[];
  loading: boolean;
  hasError: boolean;
  decisionId: string;
};

export function useDyRecommendations({
  categoryTitle,
  recommendationChannel,
}: DyRecommendationParams): UseDyRecommendationsResult {
  const { refinements } = React.useContext(RefinementsContext);
  const { refinementsChangedDeep$ } = useRefinementsEvents();
  const { client, context } = React.useContext(DyContext);

  const { wishlistHelper } = React.useContext(WishlistHelperContext);
  const [currentCategory, setCurrentCategory] = React.useState<string | null>(null);

  const [{ recommendations, title, decisionId, loading, hasError }, dispatch] = React.useReducer(reducer, initialState);
  const { clickoutSubject } = React.useContext(ClickoutContext);

  const recommendationRules = createDyRecommendationRules(categoryTitle, refinements);

  const dyCampaign = dyRecommendationCampaigns[recommendationChannel];
  const fetchRecommendations = React.useCallback(() => {
    dispatch({ type: 'LOADING_STARTED' });
    client
      .recommendations({ recommendationRules, campaignName: dyCampaign, context })
      .then(({ recommendations, title, decisionId }) => {
        dispatch({
          type: 'LOADING_COMPLETED',
          recommendations,
          title,
          decisionId,
        });
      })
      .catch(() => {
        dispatch({ type: 'LOADING_ERRORED' });
      });
  }, [client, context, dyCampaign, recommendationRules]);

  // initial recommendation loading / CATEGORY change
  React.useEffect(() => {
    if (currentCategory !== categoryTitle) {
      fetchRecommendations();
      setCurrentCategory(categoryTitle);
    }
  }, [categoryTitle, currentCategory, fetchRecommendations]);

  // react to REFINEMENTS change
  React.useEffect(() => {
    const refinementsChangedSub = refinementsChangedDeep$.subscribe(() => {
      fetchRecommendations();
    });

    return refinementsChangedSub.unsubscribe;
  }, [refinementsChangedDeep$, fetchRecommendations]);

  // react to WISHLISTED or removed from wishlist
  React.useEffect(() => {
    let subscription: Subscription | null = null;
    if (dyCampaign !== DyRecommendationCampaign.WISHLIST) {
      subscription = wishlistHelper.subscribe(() => {
        fetchRecommendations();
      });
    }
    return () => {
      subscription && subscription.unsubscribe();
    };
  }, [dyCampaign, fetchRecommendations, wishlistHelper]);

  // react to CLICKOUTS
  React.useEffect(() => {
    const subscription = clickoutSubject.subscribe(() => {
      fetchRecommendations();
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [clickoutSubject, fetchRecommendations]);

  return {
    recommendations,
    title,
    loading,
    hasError,
    decisionId,
  };
}
